// Resolution of single-TARGET automation ops (the picked-card half of an
// interactive effect: "kill target minion", "deal 2 to target", "tap target").
//
// Split from automationRuntime's executeTargetOp so the SAME resolution runs on
// both sides of the wire: the client routes through it with a mesh/animation host
// adapter, the server with a state-mutation host adapter (automationChoice's
// applyResume). The PICK itself (target halos, the click) stays client-side
// interaction. The server emits a durable pendingChoice and replays the pick.
// This is the target-op analog of dispatchSilentOp: one shared decision module,
// two host adapters, no per-op server re-implementation.
//
// Pure. Owns NO GameBoard / mesh references. Every side effect, and every
// renderer-leaky read (a card's display label, its board cell, its mesh), routes
// through the `host` adapter, so a new target op = one branch here + a method on
// each host, never a GameBoard edit.
//
// /**
//  * @typedef {Object} TargetOpHost
//  * @property {(sourceCard: object) => string} cardName. Source's display name
//  * @property {(card: object) => string} cardLabel. Name-leak-safe target label
//  * @property {(key: string, values?: object) => void} logAction
//  * @property {(card: object) => boolean} hasActiveWard
//  * @property {(card: object) => void} consumeWard
//  * @property {(card: object) => void} sendToCemetery
//  * @property {(card: object, amount: number, opts: object) => void} damageTarget
//  */

import { OP } from '../schema';
import { wardBreaksOnTargetOp } from './wards';

// Target ops migrated to this shared resolver. The client delegates these from
// executeTargetOp; the server's applyResume runs them via the same path. Grow
// op-by-op. Each addition lights up on BOTH sides at once.
export const RESOLVE_TARGET_OPS = new Set([
  OP.KILL_TARGET,
  OP.DAMAGE_TARGET,
  OP.SUMMON_TOKEN_AT_TARGET,
  OP.FLOOD_TARGET_SITE,
  OP.TAP_TARGET,
  OP.BANISH_TARGET,
  OP.RETURN_TO_HAND_TARGET,
]);

// Resolve one target op against the picked `target`. `pt` is the pending-target
// record { op, args, sourceCard, controller, duration }. Returns 'ward_broken'
// when a Ward nullified the effect (the caller skips this source's remaining
// unit-targeting followups), else undefined.
export function resolveTargetOp(host, pt, target) {
  const { op, args, sourceCard } = pt;
  const sourceName = host.cardName(sourceCard);

  // Ward: a warded card breaks its ward INSTEAD of taking the targeted effect.
  // Damage + destruction break a Ward from any source; other targeting only from
  // an opponent. WardBreaksOnTargetOp encodes that. The break nullifies the
  // ENTIRE source's effect against this unit, then signals the skip.
  if (host.hasActiveWard(target) && wardBreaksOnTargetOp(op, sourceCard, target)) {
    host.consumeWard(target);
    host.logAction('{source}: ward broken on {target}', { source: sourceName, target: host.cardLabel(target) });
    return 'ward_broken';
  }

  if (op === OP.KILL_TARGET) {
    host.logAction('{source}: killed {target}', { source: sourceName, target: host.cardLabel(target) });
    host.sendToCemetery(target);
  } else if (op === OP.DAMAGE_TARGET) {
    // Unified damage path (Avatar/Site/Minion dispatch + Ward + Lethal + DD on the
    // client; ward-aware HP write on the server). Does NOT clamp at 0 (codex
    // "Zero." A 0-power minion dies from any damage >= 1).
    host.damageTarget(target, args.amount || 1, {
      sourceLabel: sourceName, log: true, sweep: true, floatingDamage: true,
    });
  } else if (op === OP.SUMMON_TOKEN_AT_TARGET) {
    // Gift of the Frog. Summon a named catalog token onto the picked card's cell
    // (destination fixed by the rule, no click-to-place).
    const tokenRow = host.findCatalogToken(args.tokenName);
    if (!tokenRow) { host.logAction('{source}: no "{token}" token in catalog', { source: sourceName, token: args.tokenName }); return undefined; }
    const cell = host.cellOfCard(target);
    if (!cell) { host.logAction('{source}: {token} has no valid cell', { source: sourceName, token: args.tokenName }); return undefined; }
    host.spawnTokenAtCell(tokenRow, !!target.rotated, cell);
    host.logAction('{source}: summoned {token} at {target}', { source: sourceName, token: args.tokenName, target: host.cardLabel(target) });
  } else if (op === OP.FLOOD_TARGET_SITE) {
    // Geyser. Flood the picked site (status 'flooded', making it a water site) and
    // give each minion atop it the keyword (Airborne). A this_turn cast records a
    // revert per granted card so the turn boundary un-floods + strips the keyword.
    const cell = host.cellOfCard(target);
    if (!cell) { host.logAction('{source}: target site has no cell. The flood fizzles', { source: sourceName }); return undefined; }
    const thisTurn = pt.duration === 'this_turn';
    if (host.grantStatus(target, 'flooded') && thisTurn) host.recordThisTurnStatus(target.id, 'flooded');
    const keyword = args.keyword;
    let granted = 0;
    if (keyword) {
      for (const minion of host.minionsAtCell(cell)) {
        if (host.effectiveStatuses(minion).includes(keyword)) continue;
        host.grantStatus(minion, keyword);
        if (thisTurn) host.recordThisTurnStatus(minion.id, keyword);
        granted += 1;
      }
    }
    host.recomputeAuras();
    const flooded = { source: sourceName, target: host.cardLabel(target) };
    if (!keyword) host.logAction('{source}: flooded {target}', flooded);
    else if (granted === 1) host.logAction('{source}: flooded {target} and gave {keyword} to 1 minion', { ...flooded, keyword });
    else host.logAction('{source}: flooded {target} and gave {keyword} to {n} minions', { ...flooded, keyword, n: granted });
  } else if (op === OP.TAP_TARGET) {
    // Grievous Insult. Tap the picked unit (idempotent; an already-tapped target
    // stays tapped). Routes through the host's unified tap so summoning-sickness /
    // skip-untap bookkeeping is single-sourced on the client.
    host.tapTarget(target);
    host.logAction('{source}: tapped {target}', { source: sourceName, target: host.cardLabel(target) });
  } else if (op === OP.BANISH_TARGET) {
    host.logAction('{source}: banished {target}', { source: sourceName, target: host.cardLabel(target) });
    host.banishTarget(target);
  } else if (op === OP.RETURN_TO_HAND_TARGET) {
    // Returns to the card's OWNER hand (an opponent's card goes to THEIR hand).
    host.returnToHand(target);
    host.logAction('{source}: returned {target} to hand', { source: sourceName, target: host.cardLabel(target) });
  }
  return undefined;
}
